import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import NotificationPanel from './NotificationPanel';
import SandboxToggle from './SandboxToggle';
import MerlinNavSwitcher from './MerlinNavSwitcher';
import PortalMoreMenu from './PortalMoreMenu';
import { splitMoreMenu } from './portalMoreMenu';
import { accessibleMerlins } from '../../constants/merlins';
import { execTabsFor, type ExecTab } from '../../constants/execPortal';
import { isExecPortalUser } from '../../utils/landingPage';
import { getActiveTab, getInitials, resolveActiveTab } from './portalNavUtils';
import { bellNewLabel } from '../../constants/notificationTypes';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import styles from './PortalNavbar.module.css';

/** ChemMasters logo — real brand image scaled to fill the navbar logo area */
function Logo() {
  return (
    <img
      src="/chemmasters-logo.png"
      alt="ChemMasters — Quality, Innovation, Service"
      className={styles.logo}
    />
  );
}

interface NavTab {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  badge?: number;
  badgeVariant?: 'danger' | 'warning';
  /** Plain link that opens in a new tab (the Metabase Data Explorer). */
  external?: boolean;
  title?: string;
}

const ICON = (d: React.ReactNode) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {d}
  </svg>
);

// Icons for the executive tab set (constants/execPortal.ts), keyed by tab id.
const EXEC_TAB_ICONS: Record<ExecTab['id'], React.ReactNode> = {
  // map / route — the roadmap
  roadmap: ICON(<><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" /><line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" /></>),
  // sunrise — the morning briefing
  briefing: ICON(<><path d="M17 18a5 5 0 0 0-10 0" /><line x1="12" y1="9" x2="12" y2="2" /><line x1="4.22" y1="10.22" x2="5.64" y2="11.64" /><line x1="1" y1="18" x2="3" y2="18" /><line x1="21" y1="18" x2="23" y2="18" /><line x1="18.36" y1="11.64" x2="19.78" y2="10.22" /><line x1="23" y1="22" x2="1" y2="22" /><polyline points="8 6 12 2 16 6" /></>),
  // activity/pulse — the quality-signals digest (same as the reader tab)
  signals: ICON(<polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />),
  // clipboard with a check — the read-only Quality desk (kanban 808dabf6)
  'quality-copilot': ICON(<><path d="M9 4h6a1 1 0 0 1 1 1v1H8V5a1 1 0 0 1 1-1z" /><path d="M8 6H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-2" /><polyline points="9 14 11 16 15 12" /></>),
  // alert triangle — quality issues
  'quality-issues': ICON(<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>),
  // award — letter grades
  'customer-grading': ICON(<><circle cx="12" cy="8" r="7" /><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" /></>),
  // dollar — costs
  costs: ICON(<><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>),
  // bar chart — analytics
  'portal-analytics': ICON(<><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></>),
  // database cylinder — the knowledge banks (same glyph as the CSR tab)
  'knowledge-banks': ICON(<><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></>),
  // external link — Metabase
  'data-explorer': ICON(<><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></>),
};

/** Top-row tab count at which the bar switches to the dense (scrolling) layout. */
export const DENSE_ROW_TABS = 8;

/** The executive navbar — derived from the registry, nothing hard-coded here;
 *  per-person tabs (the read-only Quality desk) need the explicit grant. */
function execNavTabs(explicitScopes: readonly string[] | null | undefined): NavTab[] {
  return execTabsFor(explicitScopes).map((t) => ({
    id: t.id,
    label: t.label,
    path: t.path,
    icon: EXEC_TAB_ICONS[t.id],
    external: t.external,
    title: t.title,
  }));
}

interface PortalNavbarProps {
  inboxCount?: number;
  ordersNeedReviewCount?: number;
  /** Number of action items needing attention (shown in bell badge). */
  actionItemCount?: number;
  /**
   * Fresh unread bell-only notifications. Shows a small DOT on the bell
   * when there is no numeric badge — how bell-only notifications (e.g.
   * investigation alerts) get a visual cue on the closed bell.
   */
  freshUnreadCount?: number;
  /** When true, the notification panel should be opened externally (e.g. from banner). */
  openNotificationsRequested?: boolean;
  /** Called after the panel has been opened in response to the external request. */
  onNotificationsOpened?: () => void;
  /** Called when the compose button is clicked */
  onCompose?: () => void;
  /**
   * Called whenever a notification is mutated (read/dismissed) inside the
   * NotificationPanel. Parent should re-fetch the action-item count so the
   * bell badge stays in sync with the panel.
   */
  onActionItemMutated?: () => void;
}

const PortalNavbar: React.FC<PortalNavbarProps> = ({
  inboxCount = 0,
  ordersNeedReviewCount = 0,
  actionItemCount = 0,
  freshUnreadCount = 0,
  openNotificationsRequested = false,
  onNotificationsOpened,
  onCompose,
  onActionItemMutated,
}) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Open notification panel when requested externally (e.g. from banner "View" button)
  useEffect(() => {
    if (openNotificationsRequested && !showNotifications) {
      setShowNotifications(true);
      onNotificationsOpened?.();
    }
  }, [openNotificationsRequested, showNotifications, onNotificationsOpened]);

  // Close notification panel on route change
  useEffect(() => {
    setShowNotifications(false);
  }, [location.pathname]);

  // Close notification panel on outside click
  const notifBtnRef = useRef<HTMLButtonElement>(null);
  const notifPanelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showNotifications) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (notifBtnRef.current?.contains(target)) return;
      if (notifPanelRef.current?.contains(target)) return;
      setShowNotifications(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showNotifications]);

  // Close menus on outside click
  useEffect(() => {
    const handleClick = () => setShowUserMenu(false);
    if (showUserMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [showUserMenu]);

  // Escape closes the account menu and the notifications panel, handing
  // focus back to the button that opened it (a keyboard-only tester could
  // not get out of either, proposal 9).
  const userBtnRef = useRef<HTMLButtonElement>(null);
  const closeUserMenu = useCallback(() => {
    setShowUserMenu(false);
    userBtnRef.current?.focus();
  }, []);
  useEscapeKey(closeUserMenu, showUserMenu);
  const closeNotifications = useCallback(() => {
    setShowNotifications(false);
    notifBtnRef.current?.focus();
  }, []);
  useEscapeKey(closeNotifications, showNotifications);

  const { hasScope } = useAuth();
  const canManageOrders = hasScope('orders:manage');
  const canViewOrders = hasScope('orders:read');
  const canViewInboxAnalytics = hasScope('analytics:view');
  const canUseTechSupport = hasScope('techsupport:copilot');
  const canViewSignals = hasScope('signals:read');
  // Knowledge Banks tab — the hosted KB Explorer (kb:explore grant; admins pass).
  const canExploreKbs = hasScope('kb:explore');
  const canUseSalesCopilot = hasScope('copilot:chat');
  // The executive door (PJ / Ravi): selected by the EXPLICIT exec:portal
  // grant — never the admin short-circuit — so a plain admin keeps the CSR bar.
  const isExec = !!user && isExecPortalUser(user);

  // The Merlin family (Analyst / Quality) collapses into a single top-bar entry
  // — one plain tab when the user can reach one, a specialist switcher when they
  // can reach several. Derived from the Merlin registry, so adding a Merlin needs
  // no change here.
  //
  // The Tech-Support door is intentionally NOT in that switcher: its page is an
  // email/inbox workspace with Merlin embedded, so it gets its own plain "Tech
  // Support" tab (in allTabs below), gated by techsupport:copilot. Merlin stays
  // branded inside the page. See constants/merlins.ts.
  const merlins = accessibleMerlins(hasScope);

  const allTabs: (NavTab & {
    requiresOrdersManage?: boolean;
    requiresOrdersRead?: boolean;
    requiresAnalytics?: boolean;
    requiresTechSupport?: boolean;
    requiresSignals?: boolean;
    requiresKbExplore?: boolean;
    requiresSalesCopilot?: boolean;
  })[] = [
    {
      id: 'inbox',
      label: 'Inbox',
      path: '/portal/inbox',
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
      ),
      badge: inboxCount > 0 ? inboxCount : undefined,
      badgeVariant: 'danger',
      requiresOrdersManage: true,
    },
    {
      id: 'orders',
      label: 'Orders',
      path: '/portal/orders',
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
      badge: ordersNeedReviewCount > 0 ? ordersNeedReviewCount : undefined,
      badgeVariant: 'warning',
      // Read-only order visibility: any portal persona with orders:read sees
      // the Orders tab; edit affordances inside still need orders:manage.
      requiresOrdersRead: true,
    },
    {
      id: 'customers',
      label: 'Customers',
      path: '/portal/customers',
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
        </svg>
      ),
    },
    {
      // Standing per-ship-to pricing exemptions — read-only reference so
      // CSRs can see which customers price from another account's list.
      id: 'pricing-exemptions',
      label: 'Pricing Exemptions',
      path: '/portal/pricing-exemptions',
      icon: (
        // tag — price-list exemptions
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
          <line x1="7" y1="7" x2="7.01" y2="7" />
        </svg>
      ),
      requiresOrdersRead: true,
    },
    {
      // Missed Orders (kanban fc6bca28): Merlin's verdict per missed order
      // beside PJ's grade, with the one-click correction. Same viewer gate
      // as the Orders tab (orders:read; the API enforces it); folds into More.
      id: 'missed-orders',
      label: 'Missed Orders',
      path: '/portal/missed-orders',
      icon: (
        // alert-triangle — a missed order
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      ),
      requiresOrdersRead: true,
    },
    {
      id: 'signals',
      label: 'Quality Signals',
      path: '/portal/signals',
      icon: (
        // activity/pulse — the quality-signals digest
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
      ),
      requiresSignals: true,
    },
    {
      id: 'inbox-analytics',
      label: 'Inbox Analytics',
      path: '/portal/inbox-analytics',
      icon: (
        // bar-chart — response-time analytics
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      ),
      requiresAnalytics: true,
    },
    {
      id: 'my-salespeople',
      label: 'My Salespeople',
      path: '/portal/my-salespeople',
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="8.5" cy="7" r="4" />
          <path d="M20 8v6M23 11h-6" />
        </svg>
      ),
      requiresOrdersManage: true,
    },
    {
      id: 'all-emails',
      label: 'Orders Inbox',
      path: '/portal/all-emails',
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="3" width="20" height="18" rx="2" />
          <path d="M2 8h20" />
          <path d="M8 3v5" />
        </svg>
      ),
      requiresOrdersManage: true,
    },
    {
      // Sent sits after Orders Inbox, not between Inbox and Orders: the usage
      // graph showed 1 to 2 s Sent -> Orders hops from the order desk, i.e.
      // misclicks between the two busiest tabs (ui_usage_review run 4,
      // kanban d28d76ad).
      id: 'sent',
      label: 'Sent',
      path: '/portal/sent',
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="22" y1="2" x2="11" y2="13" />
          <polygon points="22 2 15 22 11 13 2 9 22 2" />
        </svg>
      ),
      requiresOrdersManage: true,
    },
    {
      // The gurucloud-kb Knowledge Bank Explorer, hosted by the api at
      // /kb-explorer/ and embedded in the portal shell (an iframe page) so
      // staff who curate agent knowledge browse / search / edit the banks
      // without leaving the portal. Gated on kb:explore (admins pass).
      id: 'knowledge-banks',
      label: 'Knowledge Banks',
      path: '/portal/knowledge-banks',
      icon: (
        // database cylinder — the banks
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <ellipse cx="12" cy="5" rx="9" ry="3" />
          <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
          <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
        </svg>
      ),
      title: 'Knowledge Banks — browse, search, map and edit the agents\u2019 knowledge',
      requiresKbExplore: true,
    },
    {
      // Not a Merlin-family peer: this door is John Kirk's tech-support inbox
      // WORKSPACE (Merlin is embedded inside the page), so it lives here as its
      // own plain tab rather than in the Merlin switcher. See constants/merlins.ts.
      id: 'tech-support-copilot',
      label: 'Tech Support',
      path: '/portal/tech-support-copilot',
      icon: (
        // speech bubble — a support inbox
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      ),
      requiresTechSupport: true,
    },
    {
      // The standalone Sales Copilot PWA (/copilot, a sibling route in the same
      // BrowserRouter — the shared tokenService keeps the session, no re-login).
      // Gated on copilot:chat: every salesperson/manager/admin holds it by
      // default; CSR USERs don't. Surfaced per Scott's 07-10 directive so
      // salespeople can FIND the co-pilot — no scope change, no messaging.
      id: 'sales-copilot',
      label: 'Sales Co-Pilot',
      path: '/copilot',
      icon: (
        // four-point spark — an assistant, distinct from the support bubble
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 3l2 5.6 5.6 2-5.6 2L12 18.2l-2-5.6-5.6-2 5.6-2z" />
        </svg>
      ),
      requiresSalesCopilot: true,
    },
    {
      // The asks the quality desk has for this person (kanban 2a28d4c0):
      // open to every signed-in portal user; the API scopes it to them.
      id: 'questions-for-me',
      label: 'Questions for me',
      path: '/portal/questions-for-me',
      icon: (
        // speech bubble with a question mark
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          <path d="M9.5 8.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5V13" />
          <line x1="12" y1="15.5" x2="12" y2="15.6" />
        </svg>
      ),
    },
    {
      // Everyone's own slice of the roadmap (kanban 5895aca3): Merlin's
      // questions for them, their initiatives, the org chart.
      id: 'my-roadmap',
      label: 'My roadmap',
      path: '/portal/my-roadmap',
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="5" r="2.5" />
          <circle cx="5" cy="19" r="2.5" />
          <circle cx="19" cy="19" r="2.5" />
          <path d="M12 7.5v4M12 11.5H5v5M12 11.5h7v5" />
        </svg>
      ),
    },
    {
      // The agenda for the meetings this person is in (kanban 108573ba):
      // the API scopes the series to them; leadership has the same view
      // inside the Roadmap tab.
      id: 'meetings',
      label: 'Meetings',
      path: '/portal/meetings',
      icon: (
        // a numbered list — an agenda
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="9" y1="6" x2="20" y2="6" /><line x1="9" y1="12" x2="20" y2="12" /><line x1="9" y1="18" x2="20" y2="18" />
          <circle cx="4.5" cy="6" r="1" /><circle cx="4.5" cy="12" r="1" /><circle cx="4.5" cy="18" r="1" />
        </svg>
      ),
    },
  ];

  // Filter tabs based on user's scopes. Executives get the curated leadership
  // set INSTEAD of the CSR / persona tabs (the Merlin switcher stays).
  const tabs: NavTab[] = isExec
    ? execNavTabs(user?.scopes)
    : allTabs.filter(
        tab =>
          (!tab.requiresOrdersManage || canManageOrders) &&
          (!tab.requiresOrdersRead || canViewOrders) &&
          (!tab.requiresAnalytics || canViewInboxAnalytics) &&
          (!tab.requiresTechSupport || canUseTechSupport) &&
          (!tab.requiresSalesCopilot || canUseSalesCopilot) &&
          (!tab.requiresSignals || canViewSignals) &&
          (!tab.requiresKbExplore || canExploreKbs),
      );
  const activeTab = resolveActiveTab(
    getActiveTab(location.pathname),
    tabs.map((t) => t.id),
  );
  // The rarely used tabs fold into "More ▾" (portalMoreMenu.ts, usage-graph
  // finding C7). Scope filtering already happened above, so a gated tab the
  // user cannot see never reaches the menu either. The executive bar is
  // never split: its curated row stays exactly as the registry orders it
  // (its Quality Signals tab shares the 'signals' id with the folded one).
  const { primary: rowTabs, more: moreTabs } = isExec
    ? { primary: tabs, more: [] }
    : splitMoreMenu(tabs);
  // A crowded row (the executive bar, or an admin's eight-plus tabs) takes
  // the dense treatment: tighter tabs that scroll sideways instead of pushing
  // the bell and user menu off a 1280px laptop (on main an admin's bar ran
  // 2,353px wide at that width).
  const dense = isExec || rowTabs.length >= DENSE_ROW_TABS;

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  return (
    <>
      <nav className={styles.navbar}>
        {/* White logo area — aligned with sidebar width */}
        <div className={styles.logoArea}>
          <Logo />
        </div>

        {/* Dark gradient main area */}
        <div className={`${styles.mainArea}${dense ? ` ${styles.mainAreaDense}` : ''}`}>
          {/* Tab navigation */}
          <div className={`${styles.tabGroup}${dense ? ` ${styles.tabGroupDense}` : ''}`}>
            {/* Every nav control records a stable `nav_tab` row with the tab
                id as the chosen value (the recorder's fallback name would be
                role + visible text, which drifts with badge counts). */}
            {rowTabs.map(tab =>
              tab.external ? (
                <a
                  key={tab.id}
                  className={styles.tab}
                  href={tab.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={tab.title}
                  data-action="nav_tab"
                  data-value={tab.id}
                  data-record-value="tab"
                  data-outcome="navigate"
                >
                  {tab.icon}
                  {tab.label}
                </a>
              ) : (
                <button
                  key={tab.id}
                  className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
                  onClick={() => navigate(tab.path)}
                  type="button"
                  title={tab.title}
                  data-action="nav_tab"
                  value={tab.id}
                  data-record-value="tab"
                  data-outcome="navigate"
                >
                  {tab.icon}
                  {tab.label}
                  {tab.badge !== undefined && (
                    <span className={`${styles.tabBadge} ${tab.badgeVariant === 'warning' ? styles.tabBadgeWarn : ''}`}>
                      {tab.badge > 99 ? '99+' : tab.badge}
                    </span>
                  )}
                </button>
              ),
            )}
            <MerlinNavSwitcher merlins={merlins} />
            <PortalMoreMenu items={moreTabs} activeTab={activeTab} />
          </div>

          {/* Right actions */}
          <div className={styles.actions}>
            {/* The sandbox email-redirect switch is an order-desk / admin tool;
                executives (who may be ADMIN-role) never need it in their bar. */}
            {!isExec && <SandboxToggle />}

            {canManageOrders && !isExec && (
              <button
                className={styles.iconBtn}
                type="button"
                aria-label="Compose new email"
                onClick={() => onCompose ? onCompose() : navigate('/portal/inbox')}
                title="Compose"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            )}

            <button
              ref={notifBtnRef}
              className={styles.iconBtn}
              type="button"
              data-action="portal_notifications_toggle"
              aria-label={
                bellNewLabel(actionItemCount, freshUnreadCount)
                  ? `Notifications, ${bellNewLabel(actionItemCount, freshUnreadCount)}`
                  : 'Notifications'
              }
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {actionItemCount > 0 && (
                <span className={styles.notifBadge}>
                  {actionItemCount > 99 ? '99+' : actionItemCount}
                </span>
              )}
              {/* A count in words, not a bare dot (proposal 10). */}
              {bellNewLabel(actionItemCount, freshUnreadCount) && (
                <span className={styles.notifNew} data-testid="bell-new">
                  {bellNewLabel(actionItemCount, freshUnreadCount)}
                </span>
              )}
            </button>

            <div className={styles.divider} />

            {/* User button */}
            <button
              className={styles.userBtn}
              type="button"
              ref={userBtnRef}
              aria-haspopup="menu"
              aria-expanded={showUserMenu}
              data-testid="account-menu-button"
              onClick={(e) => { e.stopPropagation(); setShowUserMenu(!showUserMenu); }}
            >
              <div className={styles.avatar}>
                {user ? getInitials(user.name || user.email) : '??'}
              </div>
              <span className={styles.userName}>
                {user?.name?.split(' ')[0] || 'User'}
              </span>
              <svg className={styles.chevron} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {showUserMenu && (
              <div className={styles.userMenu} role="menu" aria-label="Account">
                <div className={styles.menuHeader}>
                  <div className={styles.menuName}>{user?.name}</div>
                  <div className={styles.menuEmail}>{user?.email}</div>
                </div>
                <div className={styles.menuDivider} />
                <button
                  className={styles.menuItem}
                  role="menuitem"
                  onClick={handleLogout}
                  type="button"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      <NotificationPanel
        ref={notifPanelRef}
        isOpen={showNotifications}
        onClose={() => {
          setShowNotifications(false);
        }}
        onMutate={onActionItemMutated}
      />
    </>
  );
};

export default PortalNavbar;
